import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "vexyora_cookie_consent";

const CONSENT_DEFAULT = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
  functionality_storage: "granted",
  security_storage: "granted",
};

function updateGtagConsent(state) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;
  window.gtag("consent", "update", state);
}

export default function CookieBanner({ openSignal, onClosePrefs }) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      setVisible(true);
    } else {
      try {
        const parsed = JSON.parse(saved);
        setPrefs({ analytics: !!parsed.analytics, marketing: !!parsed.marketing });
      } catch {
        setVisible(true);
      }
    }
  }, []);

  // Allow footer button to reopen preferences dialog
  useEffect(() => {
    if (openSignal > 0) {
      setShowPrefs(true);
      setVisible(true);
    }
  }, [openSignal]);

  const persist = (analytics, marketing) => {
    const state = {
      ad_storage: marketing ? "granted" : "denied",
      ad_user_data: marketing ? "granted" : "denied",
      ad_personalization: marketing ? "granted" : "denied",
      analytics_storage: analytics ? "granted" : "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    };
    updateGtagConsent(state);
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ analytics, marketing, ts: new Date().toISOString() })
    );
  };

  const acceptAll = () => {
    persist(true, true);
    setVisible(false);
    setShowPrefs(false);
    onClosePrefs && onClosePrefs();
  };
  const rejectAll = () => {
    persist(false, false);
    setVisible(false);
    setShowPrefs(false);
    onClosePrefs && onClosePrefs();
  };
  const savePrefs = () => {
    persist(prefs.analytics, prefs.marketing);
    setVisible(false);
    setShowPrefs(false);
    onClosePrefs && onClosePrefs();
  };

  if (!visible) return null;

  if (showPrefs) {
    return (
      <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-4" data-testid="cookie-prefs-dialog">
        <div className="bg-white w-full max-w-2xl" style={{ borderTop: "3px solid var(--vx-emerald)" }}>
          <div className="p-6 sm:p-8">
            <h3 className="font-serif-display text-2xl font-bold" style={{ color: "var(--vx-navy)" }}>{t("cookie_banner.preferences_title")}</h3>
            <p className="mt-2 text-sm" style={{ color: "var(--vx-graphite)" }}>{t("cookie_banner.preferences_body")}</p>

            <div className="mt-6 space-y-4">
              <div className="border p-4" style={{ borderColor: "var(--vx-border)" }}>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm font-semibold" style={{ color: "var(--vx-navy)" }}>{t("cookie_banner.essential")}</div>
                    <div className="text-xs mt-1" style={{ color: "var(--vx-graphite-light)" }}>{t("cookie_banner.essential_desc")}</div>
                  </div>
                  <span className="vx-eyebrow">ON</span>
                </div>
              </div>

              <label className="border p-4 flex items-start justify-between gap-4 cursor-pointer" style={{ borderColor: "var(--vx-border)" }}>
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--vx-navy)" }}>{t("cookie_banner.analytics")}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--vx-graphite-light)" }}>{t("cookie_banner.analytics_desc")}</div>
                </div>
                <input
                  type="checkbox"
                  checked={prefs.analytics}
                  onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                  data-testid="pref-analytics"
                  className="mt-1 w-5 h-5 accent-emerald-600"
                />
              </label>

              <label className="border p-4 flex items-start justify-between gap-4 cursor-pointer" style={{ borderColor: "var(--vx-border)" }}>
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--vx-navy)" }}>{t("cookie_banner.marketing")}</div>
                  <div className="text-xs mt-1" style={{ color: "var(--vx-graphite-light)" }}>{t("cookie_banner.marketing_desc")}</div>
                </div>
                <input
                  type="checkbox"
                  checked={prefs.marketing}
                  onChange={(e) => setPrefs({ ...prefs, marketing: e.target.checked })}
                  data-testid="pref-marketing"
                  className="mt-1 w-5 h-5 accent-emerald-600"
                />
              </label>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
              <button type="button" className="vx-btn-outline" onClick={rejectAll} data-testid="prefs-reject">
                {t("cookie_banner.reject")}
              </button>
              <button type="button" className="vx-btn-primary" onClick={savePrefs} data-testid="prefs-save">
                {t("cookie_banner.save")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="vx-cookie fixed bottom-0 left-0 right-0 z-[60]" data-testid="cookie-banner">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1">
            <div className="text-white font-semibold mb-1">{t("cookie_banner.title")}</div>
            <p className="text-sm text-slate-300 leading-relaxed">{t("cookie_banner.body")}</p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button type="button" className="vx-btn-ghost-light" onClick={() => setShowPrefs(true)} data-testid="cookie-customize">
              {t("cookie_banner.customize")}
            </button>
            <button type="button" className="vx-btn-ghost-light" onClick={rejectAll} data-testid="cookie-reject">
              {t("cookie_banner.reject")}
            </button>
            <button type="button" className="vx-btn-primary" onClick={acceptAll} data-testid="cookie-accept">
              {t("cookie_banner.accept")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
